import React, { useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { useLanguage } from "../contexts/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ContactModal from "../components/ContactModal";
import SEOHead from "../components/SEOHead";
import { useSpamSchutz, Honigtopf } from "../components/SpamSchutz";
import { 
  BookOpen, 
  Download, 
  ExternalLink,
  Mail,
  FileText,
  Star,
  ArrowRight,
  CheckCircle,
  Loader2,
  X,
  AlertCircle,
  Globe,
  Users,
  Shield,
  Scale,
  Lock,
  Eye,
  Check
} from "lucide-react";

const KnowledgeCenter = () => {
  const { t, currentLanguage } = useLanguage();
  const { honigtopf, spamFelder } = useSpamSchutz();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const seoData = {
    title: "Whitepapers & Studien - KI Expertise | BROVKO",
    description: "Kostenlose KI-Whitepapers, Fallstudien und Ressourcen zur KI-Transformation. Laden Sie unsere Expertenmaterialien herunter.",
    keywords: "KI Whitepaper, AI Ressourcen, KI-Transformation Leitfaden, BROVKO Expertise",
    canonicalUrl: "/wissenscenter",
    ogTitle: "Whitepapers & Studien - KI Expertise",
    ogDescription: "Kostenlose KI-Whitepapers und Ressourcen zur erfolgreichen AI-Transformation."
  };

  // Modal handlers
  const openContactModal = () => {
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };
  const [selectedWhitePaper, setSelectedWhitePaper] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const [downloadInfo, setDownloadInfo] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: ''
  });
  const [formErrors, setFormErrors] = useState({});
  
  // KI-Radar Newsletter State
  const [radarFormData, setRadarFormData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [radarSubmitting, setRadarSubmitting] = useState(false);
  const [radarStatus, setRadarStatus] = useState(null); // 'success', 'error', null
  const [radarErrors, setRadarErrors] = useState({});

  const books = [
    {
      id: 1,
      title: t('knowledge.books.book1.title'),
      subtitle: t('knowledge.books.book1.subtitle'),
      description: t('knowledge.books.book1.description'),
      amazonLink: currentLanguage === 'de' ? "https://amzn.eu/d/9tDGJDu" : "https://amzn.eu/d/fsqg3QP",
      image: currentLanguage === 'de' 
        ? "/assets/images/cover-ki-rev-de.avif" 
        : "/images/books/ai-revolution-en.png"
    },
    {
      id: 2,  
      title: t('knowledge.books.book2.title'),
      subtitle: t('knowledge.books.book2.subtitle'),
      description: t('knowledge.books.book2.description'),
      amazonLink: currentLanguage === 'de' ? "https://amzn.eu/d/fNTI1Ji" : "https://amzn.eu/d/6tSQmAm",
      image: currentLanguage === 'de' 
        ? "/assets/images/cover-ki-potential-de.avif" 
        : "/images/books/ai-potential-en.png"
    }
  ];

    const whitepapers = [
    {
      id: 1,
      number: "#1 07/2024",
      title: "KI in Unternehmen: Erfolgsfaktoren und Fallstudien",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/7cbd2547-b477-3ac1-7a59-4c33d13722a8/20240729_1._Whitepaper_KI_Erolgsfaktoren_Fallstudien_DE.01.pdf"
    },
    {
      id: 2,
      number: "#2 08/2024",
      title: "Die KI-Transformation: Der Chief Artificial Intelligence Officer",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/b76324c6-155d-89b0-459a-c96c44f9d408/20240805_2._WP_CAIO_KI_Architekt_DE.01.pdf"
    },
    {
      id: 3,
      number: "#3 09/2024",
      title: "Die KI-Revolution: Fokus CEO als Treiber der KI-Transformation",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/788169a6-307f-45f7-bc0b-167a36dbfa0f/240906_White_Paper_CEO_DE.pdf"
    },
    {
      id: 4,
      number: "#4 09/2024",
      title: "KI & Private Equity/M&A: Fokus Relevante Einsatzbereiche",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/0230168f-30a0-21f8-5bc3-f5193f11c424/240926_White_Paper_Private_Equity_DE.pdf"
    },
    {
      id: 5,
      number: "#5 10/2024",
      title: "KI-Fokus: Generative KI: Einsatzpotentiale in Unternehmen",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/5a6ddc1b-367a-f308-ea07-0951efdd9616/2401001__5_WP_Generative_KI_vf_DE.pdf"
    },
    {
      id: 6,
      number: "#6 10/2024",
      title: "KI & Prozessoptimierung: Wie man Process-Intelligence optimal umsetzen kann",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/b50101b6-ce0f-0f13-abce-7919cdf88f52/241008__6_24_WP_Process_Intelligence_vDE.pdf"
    },
    {
      id: 7,
      number: "#7 10/2024",
      title: "KI & Private Equity: Fokus Wertsteigerung von Portfolio-Unternehmen",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/60aa7460-ba6f-9aa8-7e40-006c7cbe5ac5/2410014__7_24_WP_PE_Wertsteigerung_DE.pdf"
    },
    {
      id: 8,
      number: "#8 10/2024",
      title: "KI-Potentiale zur Optimierung und Präzisierung von Prognose-Aktivitäten",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/e2cc382c-b993-2df7-a327-35d0498aa188/241017__8_24_WP_KI_Prognosen_vDE.pdf"
    },
    {
      id: 9,
      number: "#9 10/2024",
      title: "KI-Einsatz im Automotive-Sektor: Einsatzbereiche und Nutzen",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/db79c13e-96fc-ca46-ca10-e82688165e66/241017__9_24_WP_KI_in_Automotive_vnDE.pdf"
    },
    {
      id: 10,
      number: "#10 10/2024",
      title: "KI: IFO-KI-Studie: Aktuelle Bedenken in Unternehmen und Empfehlungen",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/c73c24f0-6c6e-e816-5331-86b2d4809348/241027__10_24_WP_KI_Barrieren_u_berwinden_vDE2.pdf"
    },
    {
      id: 11,
      number: "#11 10/2024",
      title: "KI-Einsatz in E-Commerce-Unternehmen: Einsatzbereiche und Wirkungen",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/3aa5304f-cf51-87b2-1299-c68cb669219d/241031__11_24_WP_eCommerce_v_DE.pdf"
    },
    {
      id: 12,
      number: "#12 10/2024",
      title: "Die Zukunft des Einzelhandels: Fokus KI",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/7d135af6-cdfb-047a-f8ff-9fe9c54d30c6/241031__12_24_WP_Retail_v_DE.pdf"
    },
    {
      id: 13,
      number: "#13 11/2024",
      title: "KI & Kostensenkung: Wie man mit Hilfe der KI deutliche Kostensenkungen schafft",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/ea0d4778-d9be-5578-37b3-8a8d1210a3d0/241105__13_24_WP_KI_Kostensenkung_v_DE.01.pdf"
    },
    {
      id: 14,
      number: "#14 11/2024",
      title: "KI & Umsatzsteigerung: Wie man mit KI Umsatzverbesserungen schafft",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/ed856c02-6ee2-3275-cc0c-a9c59d72cc9c/241111__14_24_WP_Umsatzsteigerung_v_DE.01.pdf"
    },
    {
      id: 15,
      number: "#15 12/2024",
      title: "KI & Data Intelligence: Maximales Kapital aus Unternehmensdaten schlagen",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/9d6cfcf2-da28-bb21-b3d1-33122ed360bc/241204__15_24_WP_Data_Intelligence_v02DE.pdf"
    },
    {
      id: 16,
      number: "#16 12/2024",
      title: "KI & Pricing: Wie man mit KI den EBIT deutlich verbessern kann",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/1891ebc4-fa63-24ea-f0f5-3978e4edd512/241210__16_24_WP_Pricing_vDE.01.pdf"
    },
    {
      id: 17,
      number: "#17 02/2025",
      title: "KI & digitale Kundenportale: Wie man digitale Portale verbessern kann",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/63d99565-e89b-0147-3b25-09a613a20f68/250217__17_KI_Kundenportale_DE.pdf"
    },
    {
      id: 18,
      number: "#18 03/2025",
      title: "KI-Transformation: Tips und Erfolgsfaktoren",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/e8b0517c-c602-a57c-c004-2d57c62a03c8/250324__18_KI_Transformation_DE.pdf"
    },
    {
      id: 19,
      number: "#19 03/2025",
      title: "KI-Strategie und KI-Governance: Die Basis für erfolgreiche KI-Transformation",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/8389527c-21a0-8ec3-bf5e-d88c86284ad7/250328__19_KI_Strategie_u._Governance_DE.04.pdf"
    },
    {
      id: 20,
      number: "#20 04/2025",
      title: "KI-Technik und -Architektur: Optimale Grundlage für skalierbare KI",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/027bb84c-4882-dfdd-54a6-971d31230394/250407__20_KI_Technik_amp_Architektur_DE.01.pdf"
    },
    {
      id: 21,
      number: "#21 04/2025",
      title: "KI-Fokus: Welche Fertigkeiten benötigt ein CEO im Umfeld der KI-Transformation",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/e62fcf4b-5493-636a-c7da-c16714db1274/250420__21_7_KI_Fa_higkeiten_DE.pdf"
    },
    {
      id: 22,
      number: "#22 04/2025",
      title: "KI: Warum jetzt starten",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/c33cdfa7-8f59-9876-89d3-50918ec4dacf/250428__22_Warum_KI_vDE.pdf"
    },
    {
      id: 23,
      number: "#23 05/2025",
      title: "KI-Resultate in Unternehmen: Beispiele für Effekte nach 90/130/150 Tagen",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/b4570fe1-6391-64ca-fb5e-b215c8551f4b/250506__23_KI_Impact_vDE.pdf"
    },
    {
      id: 24,
      number: "#24 05/2025",
      title: "KI-Orchestrierung: Wie man die KI im Unternehmen zentral verwaltet",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/e1d64a1f-f12b-bf0c-32b1-8352e0b286b2/250518__24_KI_Orchestrierung_vDE.pdf"
    },
    {
      id: 25,
      number: "#25 05/2025",
      title: "KI in Private Equity Unternehmen: Der BROVKO Ansatz zur Optimierung der PE-Wertschöpfungskette",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/6cdff3f6-6df5-3ed9-6017-d34eb10ff096/250527__25_Private_Equity_DE.pdf"
    },
    {
      id: 26,
      number: "#26 07/2025",
      title: "KI-Einsatz in schweizer Unternehmen",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/8e19e817-c099-bf27-64c3-24162d961459/250714__27_CH_Sicher_starten_vDE.pdf"
    },
    {
      id: 27,
      number: "#27 07/2025",
      title: "KI-Einsatz in österreichischen Unternehmen",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/0c8861f0-e01b-0063-3a62-b8208cbf4a7f/250714__28_AT_Sicher_starten_vDE.pdf"
    },
    {
      id: 28,
      number: "#28 07/2025",
      title: "KI in Private Equity Unternehmen: 5 top-geeignete Einsatzfelder",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/29486f29-783e-5cd5-0796-e27fe90ddc3a/250714__29_PE_vDE.pdf"
    },
    {
      id: 29,
      number: "#29 07/2025",
      title: "AIWizz – Unsere neue Enterprise-KI-Plattform für wirksamen KI-Einsatz",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/57a1b7d7-fb91-0ee6-35fa-379ec91e0dfd/250714__30_AIWizz_vDE.pdf"
    },
    {
      id: 30,
      number: "#30 07/2025",
      title: "KI im Vertrieb: Positive Effekte durch AIWizz und VoiceWizz",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/bea8d7fa-b9f3-4d6b-dee6-0dc950347cf7/250715__31_KI_im_Vertrieb_vDE.pdf"
    },
    {
      id: 31,
      number: "#31 07/2025",
      title: "KI und Fachkräftemangel: Fokus Automotive und Maschinenbau",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/3db0d7c3-97a4-546b-8050-7ed89b370dcd/250715__32_KI_im_Maschienenbau_vDE.01.pdf"
    },
    {
      id: 32,
      number: "#32 08/2025",
      title: "AIWizz – Unsere neue KI-Steuerzentrale für Ihr Unternehmen",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/295188b4-8d5e-23d1-5fbd-66bd8a1005c1/250804__33_AiWizz_vDE.pdf"
    },
    {
      id: 33,
      number: "#33 08/2025",
      title: "AiWizz Einsatz in Private Equity Unternehmen: Von Buy & Build bis zum Exit",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/736a8edb-c171-2e0f-7d39-d5515d92b3c5/250804__34_AiWizz_PE_vDE.01.pdf"
    },
    {
      id: 34,
      number: "#34 08/2025",
      title: "KI & Best Practices in der Schweiz: Lernen von den KI-Vorreitern",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/2643784c-5563-6b92-9928-03e38ae04fbb/250807__35_CH_Best_Practice_vDE.pdf"
    },
    {
      id: 35,
      number: "#35 08/2025",
      title: "ChatGPT 5.0: Was neu ist und was sich damit alles verändert",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/c6caa86d-42d6-e0ba-e27d-e5f2c16ff6ab/2508011_36_GPT_5_vDE.01.pdf"
    },
    {
      id: 36,
      number: "#36 08/2025",
      title: "VoiceWizz: Unsere KI-Sprachassistent-Lösung für Vertrieb, Service & Management",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/fbed38d2-0251-122a-6413-9d2301bd6f07/2508021_37_WizzVoice_vDE.pdf"
    },
    {
      id: 37,
      number: "#37 08/2025",
      title: "CEO kompakt: Warum 95% der IT-Projekte scheitern",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/4a5a4325-2599-b6fe-3bb5-22bad538bd8f/2508025_38_CEO_Kompakt_vDE.pdf"
    },
    {
      id: 38,
      number: "#38 09/2025",
      title: "KI-Transformation 2026: Der einzigartige Hebel für Unternehmen in 2026",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/739ab3ed-574e-aa80-eb01-57e24651b86c/250901_39_KI_Transformation_vDE.pdf"
    },
    {
      id: 39,
      number: "#39 09/2025",
      title: "600 KI-Fälle aus der Praxis: Warum jetzt mit KI starten?",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/94526031-10b2-e882-72d6-c24c26cc80ed/250910_40_KI_Starten_600_Fa_lle_vDE.pdf"
    },
    {
      id: 40,
      number: "#40 09/2025",
      title: "KI & Kostensenkung 2026: KI als schnellster Hebel zur Kostensenkung",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/d8818a6f-c3f6-f4a4-c48e-6b4f76bce9c4/250915_42_Kostensenkung_vDE.01.pdf"
    },
    {
      id: 41,
      number: "#41 10/2025",
      title: "KI & Organisation: Warum CEOs handeln müssen",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/90b225b3-63f2-3d5c-bde8-9be58294d3c2/251015_43_WP_KI_amp_Organisation_vDE.pdf"
    },
    {
      id: 42,
      number: "#42 10/2025",
      title: "Human in the Loop: Neue Arbeitsteilung zwischen Menschen und KI",
      pdfUrl: "https://mcusercontent.com/eb85b7102d507901166634a63/files/01369f31-820d-7aae-e40d-768bba2de802/251015_44_WP_Human_in_the_Loop_vDE.pdf"
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = t('knowledge.download.errors.firstName');
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = t('knowledge.download.errors.lastName');
    }
    
    if (!formData.email.trim()) {
      errors.email = t('knowledge.download.errors.email');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t('knowledge.download.errors.emailInvalid');
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleWhitepaperDownload = async (whitepaper) => {
    setSelectedWhitePaper(whitepaper);
    setSubmitStatus(null);
    setDownloadInfo(null);
    setFormErrors({});
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/whitepaper`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...(await spamFelder()),
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          company: formData.company || null,
          whitePaperId: selectedWhitePaper.id.toString(),
          whitePaperTitle: selectedWhitePaper.title,
          whitePaperNumber: selectedWhitePaper.number,
          whitePaperPdfUrl: selectedWhitePaper.pdfUrl,
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus('success');
        setDownloadInfo({
          downloadUrl: result.downloadUrl,
          leadId: result.leadId,
          title: selectedWhitePaper.title
        });
        
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          company: ''
        });
      } else {
        throw new Error(result.message || 'Failed to process request');
      }
    } catch (error) {
      // Error handled silently in production
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadClick = async () => {
    if (!downloadInfo?.downloadUrl) return;
    
    try {
      // Use the direct PDF URL from MailChimp
      const downloadUrl = downloadInfo.downloadUrl;
      
      // Trigger actual PDF download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Show success message
      alert(t('knowledge.download.downloadStarted'));
      
      // Close modal after successful download
      setTimeout(() => {
        setSelectedWhitePaper(null);
        setDownloadInfo(null);
        setSubmitStatus(null);
      }, 1500);
    } catch (error) {
      console.error('Download error:', error);
      alert(t('knowledge.download.downloadError'));
    }
  };

  const closeModal = () => {
    setSelectedWhitePaper(null);
    setDownloadInfo(null);
    setSubmitStatus(null);
    setFormErrors({});
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      company: ''
    });
  };

  // KI-Radar Newsletter Functions
  const handleRadarInputChange = (e) => {
    const { name, value } = e.target;
    setRadarFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear field error when user starts typing
    if (radarErrors[name]) {
      setRadarErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    if (radarStatus) {
      setRadarStatus(null);
    }
  };

  const validateRadarForm = () => {
    const errors = {};
    
    if (!radarFormData.firstName.trim()) {
      errors.firstName = t('knowledge.radar.newsletter.errors.firstName');
    }
    
    if (!radarFormData.lastName.trim()) {
      errors.lastName = t('knowledge.radar.newsletter.errors.lastName');
    }
    
    if (!radarFormData.email.trim()) {
      errors.email = t('knowledge.radar.newsletter.errors.email');
    } else if (!/\S+@\S+\.\S+/.test(radarFormData.email)) {
      errors.email = t('knowledge.radar.newsletter.errors.emailInvalid');
    }
    
    setRadarErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleRadarSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateRadarForm()) {
      return;
    }
    
    setRadarSubmitting(true);
    setRadarStatus(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/ki-radar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...(await spamFelder()),
          firstName: radarFormData.firstName,
          lastName: radarFormData.lastName,
          email: radarFormData.email
        }),
      });

      const result = await response.json();

      if (result.success) {
        setRadarStatus('success');
        setRadarFormData({
          firstName: '',
          lastName: '',
          email: ''
        });
        setRadarErrors({});
        
        // Auto-hide success message after 7 seconds
        setTimeout(() => {
          setRadarStatus(null);
        }, 7000);
      } else {
        // Handle specific errors from backend
        if (result.message) {
          // Subscription error handled in UI
          // You could show specific error messages here if needed
        }
        setRadarStatus('error');
        
        // Auto-hide error after 5 seconds
        setTimeout(() => {
          setRadarStatus(null);
        }, 5000);
      }
      
    } catch (error) {
      // Network error handled in UI
      setRadarStatus('error');
      
      // Auto-hide error after 5 seconds
      setTimeout(() => {
        setRadarStatus(null);
      }, 5000);
    } finally {
      setRadarSubmitting(false);
    }
  };

  return (
    <div className="theme-brand bg-surface text-ink min-h-screen bg-surface-sunken">
      <SEOHead {...seoData} />
      <Header />
      
      {/* Full Screen Success Modal for KI-Radar Newsletter */}
      {radarStatus === 'success' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-2xl p-12 mx-4 max-w-2xl w-full text-center shadow-2xl">
            <CheckCircle className="h-24 w-24 text-green-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-ink mb-4">
              Erfolgreich angemeldet!
            </h2>
            <p className="text-xl text-ink-muted mb-8 leading-relaxed">
              Vielen Dank für das Abonnement unseres KI-Radar Newsletters! Sie erhalten in Kürze eine Bestätigungs-E-Mail.
            </p>
            <Button 
              onClick={() => setRadarStatus(null)}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 text-lg"
            >
              Schließen
            </Button>
          </div>
        </div>
      )}
      
      {/* Hero Section */}
      <section className="pt-40 pb-10 bg-gradient-to-br from-slate-900 to-teal-900">
        <div className="container mx-auto px-4">
          <div className=" text-white">
            <h1 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] mb-4">
              {t('knowledge.hero.title')}
            </h1>
            <p className="text-base md:text-lg text-ink-muted max-w-4xl mx-auto">
              {t('knowledge.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Books Section */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-8">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-3">
              {t('knowledge.books.title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {books.map((book) => (
              <Card key={book.id} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-5">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-shrink-0">
                      <img 
                        src={book.image} 
                        alt={book.title}
                        className="w-28 h-40 object-cover rounded-lg shadow-lg"
                        onLoad={(e) => {
                          // Book cover loaded successfully
                          e.target.style.display = 'block';
                          e.target.nextSibling.style.display = 'none';
                        }}
                        onError={(e) => {
                          // Book cover fallback
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div 
                        className="w-28 h-40 bg-gradient-to-br from-brand to-blue-600 rounded-lg flex items-center justify-center shadow-lg"
                        style={{ display: 'flex' }}
                      >
                        <BookOpen className="h-12 w-12 text-white" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-ink mb-1">
                        {book.title}
                      </h3>
                      <p className="text-brand-400 text-sm font-semibold mb-2">
                        {book.subtitle}
                      </p>
                      <p className="text-ink-muted text-sm mb-4 leading-relaxed">
                        {book.description}
                      </p>
                      <a
                        href={book.amazonLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button className="bg-brand hover:bg-brand-600 text-ink-onBrand text-sm">
                          {t('knowledge.books.amazonButton')}
                          <ExternalLink className="ml-2 h-3 w-3" />
                        </Button>
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* KI-Radar Section */}
      <section className="py-20 md:py-28 bg-brand-spread">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Left Column - Content */}
              <div>
                <div className="inline-flex items-center bg-blue-500/15 text-blue-300 px-3 py-1.5 rounded-full text-xs font-semibold mb-4">
                  <Star className="h-3 w-3 mr-1.5" />
                  {t('knowledge.radar.badge')}
                </div>
                
                <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-4">
                  {t('knowledge.radar.title')}
                </h2>
                
                <p className="text-base text-ink-muted mb-6 leading-relaxed">
                  {t('knowledge.radar.subtitle')}
                </p>

                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="bg-blue-500/15 p-2 rounded-lg flex-shrink-0">
                      <Globe className="h-5 w-5 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-ink text-sm mb-1">
                        {t('knowledge.radar.feature1.title')}
                      </h3>
                      <p className="text-ink-muted text-xs">
                        {t('knowledge.radar.feature1.description')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="bg-brand/15 p-2 rounded-lg flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-brand-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-ink text-sm mb-1">
                        {t('knowledge.radar.feature2.title')}
                      </h3>
                      <p className="text-ink-muted text-xs">
                        {t('knowledge.radar.feature2.description')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="bg-purple-500/15 p-2 rounded-lg flex-shrink-0">
                      <Users className="h-5 w-5 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-ink text-sm mb-1">
                        {t('knowledge.radar.feature3.title')}
                      </h3>
                      <p className="text-ink-muted text-xs">
                        {t('knowledge.radar.feature3.description')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Target Audience */}
                <div className="bg-surface p-4 rounded-lg shadow-sm border border-hairline">
                  <h4 className="font-semibold text-ink text-sm mb-2">
                    {t('knowledge.radar.targetAudience.title')}
                  </h4>
                  <ul className="space-y-1.5 text-ink-muted text-xs">
                    <li className="flex items-center gap-1.5">
                      <CheckCircle className="h-3 w-3 text-green-400" />
                      {t('knowledge.radar.targetAudience.ceo')}
                    </li>
                    <li className="flex items-center gap-1.5">
                      <CheckCircle className="h-3 w-3 text-green-400" />
                      {t('knowledge.radar.targetAudience.strategists')}
                    </li>
                    <li className="flex items-center gap-1.5">
                      <CheckCircle className="h-3 w-3 text-green-400" />
                      {t('knowledge.radar.targetAudience.managers')}
                    </li>
                  </ul>
                </div>
              </div>

              {/* Right Column - Newsletter Signup */}
              <div>
                <Card className="border-0 shadow-xl bg-surface">
                  <CardContent className="p-5">
                    <div className="text-center mb-4">
                      <div className="bg-gradient-to-br from-blue-500 to-teal-500 p-3 rounded-full w-fit mx-auto mb-3">
                        <Mail className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-ink mb-1">
                        {t('knowledge.radar.newsletter.title')}
                      </h3>
                      <p className="text-ink-muted text-sm">
                        {t('knowledge.radar.newsletter.subtitle')}
                      </p>
                    </div>

                    {/* Error State */}
                    {radarStatus === 'error' ? (
                      <div className="text-center py-6">
                        <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
                        <h4 className="text-xl font-bold text-ink mb-2">
                          Anmeldung fehlgeschlagen
                        </h4>
                        <p className="text-ink-muted mb-6">
                          Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.
                        </p>
                        <Button 
                          onClick={() => setRadarStatus(null)}
                          className="bg-red-600 hover:bg-red-700 text-white"
                        >
                          Erneut versuchen
                        </Button>
                      </div>
                    ) : (
                      /* Newsletter Form */
                      <form onSubmit={handleRadarSubmit} className="space-y-4">
                        <Honigtopf {...honigtopf} />
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Input
                              type="text"
                              name="firstName"
                              placeholder={t('knowledge.radar.newsletter.firstNamePlaceholder')}
                              value={radarFormData.firstName}
                              onChange={handleRadarInputChange}
                              className={radarErrors.firstName ? "border-red-400" : ""}
                              required
                            />
                            {radarErrors.firstName && (
                              <p className="text-red-400 text-xs mt-1">{radarErrors.firstName}</p>
                            )}
                          </div>
                          <div>
                            <Input
                              type="text"
                              name="lastName"
                              placeholder={t('knowledge.radar.newsletter.lastNamePlaceholder')}
                              value={radarFormData.lastName}
                              onChange={handleRadarInputChange}
                              className={radarErrors.lastName ? "border-red-400" : ""}
                              required
                            />
                            {radarErrors.lastName && (
                              <p className="text-red-400 text-xs mt-1">{radarErrors.lastName}</p>
                            )}
                          </div>
                        </div>
                        
                        <div>
                          <Input
                            type="email"
                            name="email"
                            placeholder={t('knowledge.radar.newsletter.emailPlaceholder')}
                            value={radarFormData.email}
                            onChange={handleRadarInputChange}
                            className={radarErrors.email ? "border-red-400" : ""}
                            required
                          />
                          {radarErrors.email && (
                            <p className="text-red-400 text-xs mt-1">{radarErrors.email}</p>
                          )}
                        </div>

                        <Button
                          type="submit"
                          className="w-full bg-gradient-to-r from-blue-600 to-brand-600 hover:from-blue-700 hover:to-teal-700 text-white py-3"
                          disabled={radarSubmitting}
                        >
                          {radarSubmitting ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              {t('knowledge.radar.newsletter.submitting')}
                            </>
                          ) : (
                            <>
                              <Mail className="mr-2 h-4 w-4" />
                              {t('knowledge.radar.newsletter.subscribe')}
                            </>
                          )}
                        </Button>

                        <div className="text-center text-xs text-ink-faint">
                          {t('knowledge.radar.newsletter.privacy')}
                        </div>
                      </form>
                    )}

                    {/* Additional Info */}
                    <div className="mt-8 pt-6 border-t border-hairline">
                      <div className="grid grid-cols-2 gap-4 text-center">
                        <div>
                          <div className="text-2xl font-bold text-blue-400">2x</div>
                          <div className="text-xs text-ink-muted">
                            {t('knowledge.radar.newsletter.frequency')}
                          </div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-brand-400">15</div>
                          <div className="text-xs text-ink-muted">
                            {t('knowledge.radar.newsletter.countries')}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* White Papers Section */}
      <section id="whitepaper" className="py-20 md:py-28 bg-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-8">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-3">
              {t('knowledge.whitepapers.title')}
            </h2>
            <p className="text-base text-ink-muted max-w-[62ch]">
              {t('knowledge.whitepapers.subtitle')}
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-3">
              {whitepapers.map((paper) => (
                <Card 
                  key={paper.id} 
                  className="border-l-4 border-l-teal-500 hover:shadow-lg transition-all duration-300 cursor-pointer"
                  onClick={() => handleWhitepaperDownload(paper)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1.5">
                          <span className="bg-brand/15 text-brand-300 px-2 py-0.5 rounded-full text-xs font-semibold">
                            {paper.number}
                          </span>
                          <FileText className="h-4 w-4 text-brand-400" />
                        </div>
                        <h3 className="text-base font-semibold text-ink hover:text-brand-400 transition-colors">
                          {paper.title}
                        </h3>
                      </div>
                      <Download className="h-5 w-5 text-brand-400" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AI Regulations Section */}
      <section className="py-20 md:py-28 bg-surface-sunken">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-8">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-3">
              {t('knowledge.regulations.title')}
            </h2>
            <p className="text-base text-ink-muted max-w-4xl mx-auto mb-6">
              {t('knowledge.regulations.subtitle')}
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-3 gap-4 mb-8">
              
              {/* EU AI Act */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                <CardContent className="p-5 h-full flex flex-col">
                  <div className="mb-4">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-500/15 rounded-xl mb-3">
                      <Scale className="h-6 w-6 text-blue-400" />
                    </div>
                    <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-ink mb-2">
                      {t('knowledge.regulations.euaiact.title')}
                    </h3>
                    <div className="inline-flex items-center bg-green-500/15 text-green-300 px-2 py-0.5 rounded-full text-xs font-medium mb-3">
                      <Check className="h-3 w-3 mr-1" />
                      {t('knowledge.regulations.euaiact.status')}
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-ink mb-3">
                      {t('knowledge.regulations.euaiact.what.title')}
                    </h4>
                    <p className="text-ink-muted mb-6 leading-relaxed">
                      {t('knowledge.regulations.euaiact.what.description')}
                    </p>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-ink-muted text-sm">{t('knowledge.regulations.euaiact.benefit1')}</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-ink-muted text-sm">{t('knowledge.regulations.euaiact.benefit2')}</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-ink-muted text-sm">{t('knowledge.regulations.euaiact.benefit3')}</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-ink-muted text-sm">{t('knowledge.regulations.euaiact.benefit4')}</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-ink-muted text-sm">{t('knowledge.regulations.euaiact.benefit5')}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-auto">
                    <a
                      href="https://artificialintelligenceact.eu/de/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-blue-400 hover:text-blue-300 font-medium"
                    >
                      {t('knowledge.regulations.euaiact.link')}
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </a>
                  </div>
                </CardContent>
              </Card>

              {/* GDPR */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                <CardContent className="p-8 h-full flex flex-col">
                  <div className="mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/15 rounded-xl mb-4">
                      <Shield className="h-8 w-8 text-green-400" />
                    </div>
                    <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-ink mb-3">
                      {t('knowledge.regulations.gdpr.title')}
                    </h3>
                    <div className="inline-flex items-center bg-green-500/15 text-green-300 px-3 py-1 rounded-full text-sm font-medium mb-4">
                      <Check className="h-3 w-3 mr-1" />
                      {t('knowledge.regulations.gdpr.status')}
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-ink mb-3">
                      {t('knowledge.regulations.gdpr.what.title')} / {t('knowledge.regulations.gdpr.why.title')}
                    </h4>
                    <p className="text-ink-muted mb-6 leading-relaxed">
                      {t('knowledge.regulations.gdpr.description')}
                    </p>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-ink-muted text-sm">{t('knowledge.regulations.gdpr.benefit1')}</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-ink-muted text-sm">{t('knowledge.regulations.gdpr.benefit2')}</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-ink-muted text-sm">{t('knowledge.regulations.gdpr.benefit3')}</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-ink-muted text-sm">{t('knowledge.regulations.gdpr.benefit4')}</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-ink-muted text-sm">{t('knowledge.regulations.gdpr.benefit5')}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-auto">
                    <a
                      href="https://www.bmi.bund.de/SharedDocs/faqs/DE/themen/it-digitalpolitik/datenschutz/datenschutzgrundvo-liste.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-green-400 hover:text-green-300 font-medium"
                    >
                      {t('knowledge.regulations.gdpr.link')}
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </a>
                  </div>
                </CardContent>
              </Card>

              {/* NIS2 */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                <CardContent className="p-8 h-full flex flex-col">
                  <div className="mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-500/15 rounded-xl mb-4">
                      <Lock className="h-8 w-8 text-purple-400" />
                    </div>
                    <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-ink mb-3">
                      {t('knowledge.regulations.nis2.title')}
                    </h3>
                    <div className="inline-flex items-center bg-green-500/15 text-green-300 px-3 py-1 rounded-full text-sm font-medium mb-4">
                      <Check className="h-3 w-3 mr-1" />
                      {t('knowledge.regulations.nis2.status')}
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-ink mb-3">
                      {t('knowledge.regulations.nis2.what.title')} / {t('knowledge.regulations.nis2.why.title')}
                    </h4>
                    <p className="text-ink-muted mb-6 leading-relaxed">
                      {t('knowledge.regulations.nis2.description')}
                    </p>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-ink-muted text-sm">{t('knowledge.regulations.nis2.benefit1')}</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-ink-muted text-sm">{t('knowledge.regulations.nis2.benefit2')}</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-ink-muted text-sm">{t('knowledge.regulations.nis2.benefit3')}</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-ink-muted text-sm">{t('knowledge.regulations.nis2.benefit4')}</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-ink-muted text-sm">{t('knowledge.regulations.nis2.benefit5')}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-auto">
                    <a
                      href="https://www.bsi.bund.de/DE/Das-BSI/Auftrag/Gesetze-und-Verordnungen/NIS-Richtlinien/nis-richtlinie_node.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-purple-400 hover:text-purple-300 font-medium"
                    >
                      {t('knowledge.regulations.nis2.link')}
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Disclaimer */}
            <div className="bg-yellow-500/10 border border-yellow-400/25 rounded-lg p-6 max-w-4xl mx-auto">
              <div className="flex items-start gap-3">
                <Eye className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                <p className="text-yellow-300 text-sm leading-relaxed">
                  {t('knowledge.regulations.disclaimer')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lead Generation Modal */}
      {selectedWhitePaper && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardContent className="p-8">
              {/* Close Button */}
              <div className="flex justify-end mb-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeModal}
                  className="p-1 h-8 w-8 rounded-full hover:bg-surface-raised"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Success State */}
              {submitStatus === 'success' && downloadInfo ? (
                <div className="text-center">
                  <div className="mb-6">
                    <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
                    <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-ink mb-2">
                      {t('knowledge.download.successTitle')}
                    </h3>
                    <p className="text-ink-muted mb-4">
                      {t('knowledge.download.successMessage')}
                    </p>
                    <p className="text-sm text-ink-faint bg-surface-sunken p-3 rounded-lg">
                      "{downloadInfo.title}"
                    </p>
                  </div>
                  
                  <Button
                    onClick={handleDownloadClick}
                    className="w-full bg-brand hover:bg-brand-600 text-ink-onBrand mb-4"
                    size="lg"
                  >
                    <Download className="mr-2 h-5 w-5" />
                    {t('knowledge.download.downloadNow')}
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={closeModal}
                    className="w-full"
                  >
                    {t('knowledge.download.close')}
                  </Button>
                </div>
              ) : (
                /* Form State */
                <>
                  <div className="max-w-[64ch] mb-6">
                    <Mail className="h-12 w-12 text-brand-400 mx-auto mb-4" />
                    <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-ink mb-2">
                      {t('knowledge.download.title')}
                    </h3>
                    <p className="text-ink-muted mb-4">
                      {t('knowledge.download.subtitle')}
                    </p>
                    <div className="bg-brand/10 p-3 rounded-lg">
                      <p className="text-sm text-brand-300 font-medium">
                        "{selectedWhitePaper.title}"
                      </p>
                    </div>
                  </div>

                  {/* Error State */}
                  {submitStatus === 'error' && (
                    <div className="mb-6 p-4 bg-red-500/10 border border-red-400/25 rounded-lg">
                      <div className="flex items-center">
                        <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                        <p className="text-red-300 text-sm">
                          {t('knowledge.download.errorMessage')}
                        </p>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleFormSubmit} className="space-y-4">
                    <Honigtopf {...honigtopf} />
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Input
                          type="text"
                          name="firstName"
                          placeholder={t('knowledge.download.firstName')}
                          value={formData.firstName}
                          onChange={handleInputChange}
                          required
                          className={formErrors.firstName ? "border-red-400" : ""}
                        />
                        {formErrors.firstName && (
                          <p className="text-red-400 text-xs mt-1">{formErrors.firstName}</p>
                        )}
                      </div>
                      <div>
                        <Input
                          type="text"
                          name="lastName"
                          placeholder={t('knowledge.download.lastName')}
                          value={formData.lastName}
                          onChange={handleInputChange}
                          required
                          className={formErrors.lastName ? "border-red-400" : ""}
                        />
                        {formErrors.lastName && (
                          <p className="text-red-400 text-xs mt-1">{formErrors.lastName}</p>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <Input
                        type="email"
                        name="email"
                        placeholder={t('knowledge.download.email')}
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className={formErrors.email ? "border-red-400" : ""}
                      />
                      {formErrors.email && (
                        <p className="text-red-400 text-xs mt-1">{formErrors.email}</p>
                      )}
                    </div>
                    
                    <Input
                      type="text"
                      name="company"
                      placeholder={t('knowledge.download.company')}
                      value={formData.company}
                      onChange={handleInputChange}
                    />

                    <div className="flex gap-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={closeModal}
                        className="flex-1"
                        disabled={isSubmitting}
                      >
                        {t('knowledge.download.cancel')}
                      </Button>
                      <Button
                        type="submit"
                        className="flex-1 bg-brand hover:bg-brand-600 text-ink-onBrand"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {t('knowledge.download.submitting')}
                          </>
                        ) : (
                          <>
                            {t('knowledge.download.submit')}
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                  
                  {/* Trust Indicators */}
                  <div className="mt-6 pt-4 border-t border-hairline">
                    <div className="flex items-center justify-center text-xs text-ink-faint">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      <span>{t('knowledge.download.privacy')}</span>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-gradient-to-r from-slate-900 to-teal-900">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-3">
            {t('knowledge.cta.title')}
          </h2>
          <p className="text-base text-ink-muted max-w-[62ch] mb-6">
            {t('knowledge.cta.subtitle')}
          </p>
          <Button 
            size="lg" 
            className="bg-brand hover:bg-brand-600 text-ink-onBrand px-6 py-3 text-sm font-semibold"
            onClick={openContactModal}
          >
            {t('knowledge.cta.button')}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <Footer />

      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={closeContactModal}
        pageSource="Expertise"
        buttonContext="CTA Section"
        buttonText="Kostenloses Beratungsgespräch buchen"
      />
    </div>
  );
};

export default KnowledgeCenter;